/**
 * useCancelSubscription — Mutation hook for cancelling the caller's
 * active subscription via the `cancel-subscription` CF Worker handler.
 *
 * The handler cancels with Polar (`cancel_at_period_end: true`) so the user
 * retains access until the end of the billing period. On success the
 * subscription cache is invalidated so the UI refetches the latest state.
 *
 * DTO contract (2026-04-18):
 * Uses `fetchJsonValidated(CancelSubscriptionResultDto, ...)` so server
 * responses are validated against the shared contract. Backend/DB renames
 * can no longer drift silently — validation fails fast instead.
 *
 * Wave 4B (DTO migration): created alongside the handler DTO migration so
 * cancellation flows have a typed, schema-validated surface. Existing call
 * sites (PlanDetailsCard) can migrate to this hook incrementally.
 */
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useAuth } from '../contexts/AuthContext';
import { fetchJsonValidated } from '@dramadetector/api-client';
import {
  CancelSubscriptionResultDto,
  type CancelSubscriptionResult,
} from '@dramadetector/shared';
import { getEdgeFunctionUrl, getAuthHeaders } from '../lib/api-url';
import { subscriptionKeys } from './useSubscription';

export interface UseCancelSubscriptionReturn {
  /** Trigger cancellation — resolves with the DTO, rejects on error. */
  cancelSubscription: (args: {
    subscriptionId: string;
    reason?: string | null;
  }) => Promise<CancelSubscriptionResult>;
  /** True while the mutation is in-flight. */
  isCancelling: boolean;
  /** Last error, or null. */
  error: Error | null;
}

export function useCancelSubscription(): UseCancelSubscriptionReturn {
  const { accessToken } = useAuth();
  const queryClient = useQueryClient();

  const mutation = useMutation({
    mutationFn: async (args: {
      subscriptionId: string;
      reason?: string | null;
    }): Promise<CancelSubscriptionResult> => {
      if (!accessToken) throw new Error('Not authenticated');

      return await fetchJsonValidated(
        CancelSubscriptionResultDto,
        getEdgeFunctionUrl('cancel-subscription'),
        {
          method: 'POST',
          headers: { ...getAuthHeaders(accessToken) },
          body: JSON.stringify({
            subscriptionId: args.subscriptionId,
            reason: args.reason ?? null,
          }),
        },
      );
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: subscriptionKeys.all });
    },
  });

  return {
    cancelSubscription: mutation.mutateAsync,
    isCancelling: mutation.isPending,
    error: mutation.error as Error | null,
  };
}
